/**
 * @file Container.tsx
 * @description Flexible container component that provides consistent layout and spacing.
 * Supports background images, full-bleed layouts, configurable padding, and Contentstack Live Preview.
 */

import { CSSProperties, PropsWithChildren } from 'react';

import { IEnhancedImage } from '@/.generated';

import ImageWrapper from '@/helpers/Wrappers/ImageWrapper/ImageWrapper';
import { containerVariants } from './Container.styles';

/**
 * Props interface for Container component.
 */
interface ContainerProps {
  /** Component name for debugging (appears in data-component attribute) */
  componentName?: string;
  /** Additional CSS classes to apply */
  className?: string;
  /** Enable full-bleed layout (extends beyond parent container bounds) */
  fullBleed?: boolean;
  /** Enable bleed till page template */
  containerBleed?: boolean;
  /** Enable vertical padding (top/bottom) */
  verticalPadding?: boolean;
  /** Enable horizontal padding (left/right) */
  horizontalPadding?: boolean;
  /** Background image configuration from CMS */
  backgroundImage?: IEnhancedImage;
  /** HTML tag to render (defaults to 'section') */
  tag?: 'section' | 'div' | 'header' | 'footer';
  /** HTML id attribute */
  id?: string;
  /** Metadata id which will be used for data-id */
  metaId?: string;
  /** Additional inline styles */
  style?: CSSProperties;
  /** Apply bottom margin (mb-12). Defaults to true. Set false to remove spacing. */
  bottomMargin?: boolean;
}

/**
 * Flexible container component that wraps content with consistent layout and spacing.
 *
 * Features:
 * - Configurable padding (inline and block)
 * - Full-bleed layout option (breaks out of parent container)
 * - Background image support with positioning options
 * - Flexible HTML tag rendering
 * - Contentstack Live Preview integration
 * - Responsive design with breakpoint-specific styles
 *
 * @param {ContainerProps & PropsWithChildren} props - Component props and children
 * @returns {JSX.Element} Rendered container component
 *
 * @example
 * <Container fullBleed={true} backgroundImage={image} blockPadding={false}>
 *   <YourContent />
 * </Container>
 */
export const Container = ({
  componentName,
  className,
  fullBleed = false,
  containerBleed = false,
  verticalPadding = false,
  horizontalPadding = true,
  bottomMargin = true,
  backgroundImage,
  children,
  tag = 'section',
  id,
  metaId,
}: ContainerProps & PropsWithChildren) => {
  // Determine which HTML tag to render
  const Tag = tag;
  const hasBackgroundImage = !!backgroundImage?.image?.url;

  // Apply Tailwind variants based on props
  const { base, wrapper } = containerVariants({
    fullBleed,
    hasBackgroundImage: hasBackgroundImage,
    containerBleed,
    verticalPadding,
    horizontalPadding,
    bottomMargin,
  });

  return (
    <Tag
      className={base({ class: className })}
      data-component={componentName}
      id={id}
      data-id={`${componentName}_${metaId}`}
      aria-label={componentName}
    >
      {hasBackgroundImage && (
        <ImageWrapper
          image={{
            ...backgroundImage,
            image_fit_options: backgroundImage.image_fit_options || 'cover',
            image_position_options: backgroundImage.image_position_options || 'center',
          }}
          alt={backgroundImage.alternate_text || backgroundImage.image?.title || 'Background Image'}
          fill
          // Cover the positioned Tag so the image sits behind the content wrapper
          wrapperClassName="absolute inset-0"
          fetchPriority="high"
          loading="lazy"
        />
      )}
      <div className={wrapper()}>{children}</div>
    </Tag>
  );
};
